import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface LazyLoadImageProps {
  src: string;
  alt?: string;
  placeholderSrc?: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  width?: string | number;
  height?: string | number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

const LazyLoadImage: React.FC<LazyLoadImageProps> = ({
  src,
  alt = '',
  placeholderSrc,
  className,
  style,
  onClick,
  width,
  height,
  objectFit = 'cover',
}) => {
  const [imageSrc, setImageSrc] = useState<string>(placeholderSrc || '');
  const [imageRef, setImageRef] = useState<HTMLDivElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let observer: IntersectionObserver;

    if (imageRef) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // 뷰포트에 진입했을 때만 이미지 로드
            if (entry.isIntersecting && entry.intersectionRatio > 0) {
              setImageSrc(src);
              observer.unobserve(entry.target);
            }
          });
        },
        {
          // 이미지가 뷰포트에서 100px 전에 미리 로드 시작
          rootMargin: '100px',
          threshold: 0.01,
        }
      );

      observer.observe(imageRef);
    }

    return () => {
      if (observer && observer.unobserve && imageRef) {
        observer.unobserve(imageRef);
      }
    };
  }, [imageRef, src]);

  const handleImageLoad = () => {
    setIsLoaded(true);
    setIsError(false);
  };

  const handleImageError = () => {
    setIsError(true);
    setIsLoaded(true);
  };

  return (
    <ImageContainer
      ref={setImageRef}
      className={className}
      style={style}
      onClick={onClick}
      width={width}
      height={height}
    >
      {!isLoaded && !isError && (
        <Skeleton />
      )}

      {imageSrc && !isError && (
        <StyledImage
          src={imageSrc}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          isLoaded={isLoaded}
          objectFit={objectFit}
        />
      )}

      {isError && (
        <ErrorPlaceholder>
          <span>이미지 로드 실패</span>
        </ErrorPlaceholder>
      )}
    </ImageContainer>
  );
};

const ImageContainer = styled.div<{ width?: string | number; height?: string | number }>`
  position: relative;
  width: ${props =>
    props.width
      ? typeof props.width === 'number'
        ? `${props.width}px`
        : props.width
      : '100%'
  };
  height: ${props =>
    props.height
      ? typeof props.height === 'number'
        ? `${props.height}px`
        : props.height
      : '100%'
  };
  overflow: hidden;
  background-color: #f5f5f5;
`;

const Skeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const StyledImage = styled.img<{ isLoaded: boolean; objectFit: string }>`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.objectFit};
  opacity: ${props => props.isLoaded ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
`;

const ErrorPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  color: #999;
  font-size: 12px;

  span {
    text-align: center;
  }
`;

export default LazyLoadImage;